import React from 'react';

// App-wide error boundary. Catches any uncaught React error in the tree and
// shows a readable message instead of a blank/black screen. Also logs the
// full error to the console so debug builds and support agents can see it.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    try { window.location.reload(); } catch { /* noop */ }
  };

  handleGoHome = () => {
    try { window.location.href = '/'; } catch { /* noop */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const showDebug = typeof window !== 'undefined'
      && (window.location.search.includes('debug=1') || window.location.hostname !== 'samtale24-7.dk');

    return (
      <div
        className="min-h-screen bg-[#09090B] text-white flex items-center justify-center px-6 py-10"
        data-testid="error-boundary-screen"
      >
        <div className="max-w-md w-full space-y-6">
          <div className="w-14 h-14 rounded-full bg-destructive/20 flex items-center justify-center">
            <svg className="w-7 h-7 text-destructive" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold mb-2" data-testid="error-boundary-title">
              Der opstod en fejl
            </h1>
            <p className="text-muted-foreground text-sm">
              Siden kunne ikke indlæses korrekt. Prøv at genindlæse — hvis fejlen fortsætter, kontakt os på info@samtale24-7.dk.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={this.handleReload}
              className="flex-1 rounded-lg bg-volt text-black font-semibold px-5 py-3 hover:bg-volt/90 transition-colors"
              data-testid="error-boundary-reload-btn"
            >
              Genindlæs siden
            </button>
            <button
              onClick={this.handleGoHome}
              className="flex-1 rounded-lg border border-border font-semibold px-5 py-3 hover:bg-white/5 transition-colors"
              data-testid="error-boundary-home-btn"
            >
              Til forsiden
            </button>
          </div>
          {showDebug && this.state.error && (
            <details className="mt-6 rounded-lg border border-border bg-black/40 p-4 text-xs" open>
              <summary className="font-semibold text-yellow-300 cursor-pointer">
                Teknisk fejl-info (kun synlig i debug/preview)
              </summary>
              <pre className="mt-3 whitespace-pre-wrap break-words text-red-300" data-testid="error-boundary-stack">
                {String(this.state.error?.message || this.state.error)}
                {'\n\n'}
                {this.state.error?.stack || ''}
                {this.state.errorInfo?.componentStack ? `\n\nComponent stack:${this.state.errorInfo.componentStack}` : ''}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
